/**
 * AssigneeCell - who a job row is assigned to, and — for the roles that can
 * change that — the button that opens the assignment dialog.
 *
 * Assigning people after a work order exists is most of a dispatcher's day,
 * and until this existed the only route was Actions → Edit → scroll to the
 * Assignment card → Save the whole form. The cell that already answers
 * "who's on it?" is the natural place to answer "put someone on it": pass
 * `onAssign` and the cell becomes that button; leave it out (someone without
 * the Can Reassign Work Orders permission) and it stays the plain avatar +
 * name it was.
 *
 * Three states:
 *	 - read-only:  avatar + name, or a muted "Unassigned" — no handler given
 *	 - unassigned: a dashed "Assign" button in the brand colour. On a list
 *				   that is the row's real call to action, so it is the one
 *				   state that shouts a little.
 *	 - assigned:   the avatar + name inside a quiet outlined pill with a
 *				   pencil, so it reads as a control at rest without
 *				   competing with the row's Start / Resume CTA.
 *
 * Every interactive state is a real 44px box on touch viewports, dropping
 * to the table's dense 32px only from `desktop` up (CONVENTIONS §5). The
 * name truncates rather than pushing the row sideways: inside the phone
 * card layout `max-w-full` fits it beside its "ASSIGNEE" label, and from
 * `tablet` up — where the table is — the name itself is capped, because a
 * table cell has no width for `max-w-full` to refer to and a long name
 * would otherwise widen the whole table into a sideways scroll.
 *
 * @example
 * <AssigneeCell assigneeName={wo.assigneeName} recordLabel={`work order ${wo.id}`} onAssign={canAssign ? () => openAssign(wo) : undefined} />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface AssigneeCellProps {
	/** Display name of the primary assignee; empty/null renders the unassigned state. */
	assigneeName: string | null | undefined;
	/**
	 * Opens the assignment dialog. When omitted the cell is read-only —
	 * the caller decides who may assign, the cell just renders what it is given.
	 */
	onAssign?: () => void;
	/** What the row is, for the accessible name — e.g. "work order 9". */
	recordLabel: string;
	/** Optional extra classes merged onto the root element. */
	className?: string;
}

/* ============================================
 * PIECES
 * ============================================ */

/** The 24px initial disc every state shares. */
const Avatar: React.FC<{ name: string }> = ({ name }) => (
	<span className="assignee-cell__avatar w-24px h-24px rounded-full bg-brand-accent flex items-center justify-center flex-shrink-0" aria-hidden="true">
		<span className="text-12px font-bold text-white">{name.charAt(0).toUpperCase()}</span>
	</span>
);

/* ============================================
 * COMPONENT
 * ============================================ */

export const AssigneeCell: React.FC<AssigneeCellProps> = ({ assigneeName, onAssign, recordLabel, className = '' }) => {
	const name = (assigneeName ?? '').trim();

	/* --- Read-only: what the cell always was --- */
	if (!onAssign) {
		return name ? (
			<div className={`assignee-cell assignee-cell--static flex items-center gap-8px min-w-0 ${className}`} data-ref="molecule-assignee-cell">
				<Avatar name={name} />
				<span className="assignee-cell__name text-12px text-brand-text truncate tablet:max-w-[160px]">{name}</span>
			</div>
		) : (
			<span className={`assignee-cell assignee-cell--static assignee-cell--unassigned text-12px text-brand-text-muted ${className}`} data-ref="molecule-assignee-cell">
				Unassigned
			</span>
		);
	}

	/* --- Unassigned + can assign: the call to action --- */
	if (!name) {
		return (
			<button
				type="button"
				onClick={onAssign}
				className={`assignee-cell assignee-cell--unassigned inline-flex items-center gap-6px max-w-full min-h-44px desktop:min-h-32px px-12px desktop:px-10px rounded-6px border border-dashed border-brand-primary text-12px font-semibold text-brand-primary hover:bg-brand-primary/10 focus:outline-none focus:ring-2 focus:ring-brand-primary transition-colors ${className}`}
				data-ref="molecule-assignee-cell"
				aria-label={`Assign ${recordLabel}`}
				title="Assign"
			>
				{/* user-plus */}
				<svg className="w-16px h-16px flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
				</svg>
				Assign
			</button>
		);
	}

	/* --- Assigned + can reassign: a quiet control at rest --- */
	return (
		<button
			type="button"
			onClick={onAssign}
			className={`assignee-cell assignee-cell--assigned inline-flex items-center gap-8px max-w-full min-h-44px desktop:min-h-32px pl-6px pr-10px rounded-full border border-brand-border bg-white text-left hover:border-brand-primary hover:bg-brand-surface focus:outline-none focus:ring-2 focus:ring-brand-primary transition-colors ${className}`}
			data-ref="molecule-assignee-cell"
			aria-label={`Reassign ${recordLabel} (currently ${name})`}
			title="Reassign"
		>
			<Avatar name={name} />
			<span className="assignee-cell__name text-12px text-brand-text truncate min-w-0 tablet:max-w-[160px]">{name}</span>
			{/* pencil */}
			<svg className="w-14px h-14px flex-shrink-0 text-brand-text-subtle" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
				<path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
			</svg>
		</button>
	);
};

export default AssigneeCell;
